import {
    instructionService
} from 'shared/api/services/BEX/instruction.service';
import React, {
    useContext, useEffect, useState
} from 'react';
import {
    useParams
} from 'react-router-dom';
import {
    Context
} from 'shared/context/GlobalState';
import {
    Button
} from 'shared/components/ui/button';
import InstructionForm from './InstructionForm';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from 'shared/components/ui/dialog';
import labHelper from 'builders/builderInternal/classes/AILabHelper';
import {
    checkRequiredFields, getParameterType
} from './helper';
import {
    buildProviderParametersState
} from '../Common/providerConfigRegistry';

const UpdateInstructionDialog = ({
    openModal, setOpenModal, selectedCard, copy, handleReloadModels, modelProviders, onSuccess
}) => {

    const getInitialInstructionData = () => ({

        unique_name: '',
        model_type: 'IBM',
        modelDescription:'',
        selectedModel: '',

        instruction: '',
        context: '',
        targetFormat: '',
        sampleInput: '',
        sampleOutput: '',
        inputData: '',

        creation_timestamp: '',

        ...buildProviderParametersState({
            watsonParameters: {
                decoding_method: 'greedy',
                temperature: 0.7,
                min_new_tokens: 10,
                max_new_tokens: 200,
                top_p: 1,
                top_k: 50,
                repetition_penalty: 1.0,
                stop_sequence: ['<|endoftext|>', '<|endofcode|>'],
            }
        })
    });

    const [instructionForm, setInstructionForm] = useState(getInitialInstructionData);
    const [btnLoading, setBtnLoading] = useState(false);

    const {
        oragID, subOragID
    } = useParams();
    const {
        addNewNotifcation, user
    } = useContext(Context);

    useEffect(() => {
        if (selectedCard) {
            const extractedSegment = labHelper.extractValues(selectedCard.prompt);

            const updatedData = {
                ...getInitialInstructionData(),
                ...selectedCard,
                unique_name: copy ? '' : selectedCard.unique_name, // if copy model dont set the name
                selectedModel: selectedCard.modelId
            };
            //

            if (!extractedSegment['Instruction:'] && !extractedSegment['## Instruction']) {
                updatedData['instruction'] = selectedCard.prompt || '';
            } else {
                updatedData['instruction'] = extractedSegment['Instruction:'] || extractedSegment['## Instruction'] || '';
                updatedData['context'] = extractedSegment['Context:'] || extractedSegment['## Context'] || '';
                updatedData['targetFormat'] = extractedSegment['Target Format:'] || extractedSegment['## Target Format'] || '';
                updatedData['sampleInput'] = extractedSegment['Sample Input:'] || extractedSegment['## Sample Input'] || '';
                updatedData['sampleOutput'] = extractedSegment['Sample Output:'] || extractedSegment['## Sample Output'] || '';
                updatedData['inputData'] = '';
            }

            if (selectedCard?.model_type === 'IBM') {
                updatedData['watsonParameters'] = selectedCard?.parameters;
            } else if (selectedCard?.model_type === 'Groq') {
                updatedData['groqParameters'] = selectedCard?.parameters;
            } else if (selectedCard?.model_type === 'SambaNova') {
                updatedData['sambaNovaParameters'] = selectedCard?.parameters;
            } else if (selectedCard?.model_type === 'Cerebras') {
                updatedData['cerebrasParameters'] = selectedCard?.parameters;
            } else if (selectedCard?.model_type === 'OnPrem') {
                updatedData['onPremParameters'] = selectedCard?.parameters;
                // Store OnPrem model data if available
                if (selectedCard.api_key && selectedCard.base_url) {
                    updatedData['fullModelData'] = {
                        api_key: selectedCard.api_key,
                        base_url: selectedCard.base_url
                    };
                }
            } else if (selectedCard?.model_type === 'Gemini') {
                updatedData['geminiParameters'] = selectedCard?.parameters;
            } else if (selectedCard?.model_type === 'Mistral') {
                updatedData['mistralParameters'] = selectedCard?.parameters;
            } else if (selectedCard?.model_type === 'Novita') {
                updatedData['novitaParameters'] = selectedCard?.parameters;
            }
            //

            setInstructionForm(updatedData);
        }
    }, [selectedCard, copy]);

    const handleUpdateInstruction = async () => {
        const requiredFields = [
            {
                field: 'model_type',
                name: 'model type'
            },
            {
                field: 'unique_name',
                name: 'unique name'
            },
            {
                field: 'modelId',
                name: 'model'
            },
        ];

        const instructionObj = {
            model_type: selectedCard.model_type, // send the original type
            unique_name: instructionForm.unique_name,
            modelId: instructionForm.selectedModel,
            prompt: labHelper.ConstructPromptUnified({
                instruction: instructionForm.instruction.trim(),
                context: instructionForm.context.trim(),
                target_format: instructionForm.targetFormat.trim(),
                sample_input: instructionForm.sampleInput.trim(),
                sample_output: instructionForm.sampleOutput.trim(),
                // "input_data": inputData,
            }),
            parameters: getParameterType(instructionForm),
            org_id: oragID,
            sub_org_id: subOragID,
            modelDescription: instructionForm.modelDescription,
            user_id: user._id,
            metadata: {}
        };

        if (!copy) {
            instructionObj['instructionId'] = selectedCard._id; // add instructionId field if update
            // check if model type changed or not if changed add new_model_type to payload
            if (selectedCard.model_type !== instructionForm.model_type) {
                instructionObj['new_model_type'] = instructionForm.model_type;
                instructionObj['creation_timestamp'] = selectedCard.creation_timestamp; // add creation_timestamp field for creating new instruction in new type
            }
        }
        // check if model is IBM and api key is not set then add api key to payload
        if (instructionForm.model_type === 'IBM' && (instructionForm.api_key_id && instructionForm.level_type)) {
            instructionObj['api_key_id'] = instructionForm.api_key_id;
            instructionObj['level_type'] = instructionForm.level_type;
        }

        const checkFields = checkRequiredFields(instructionObj, requiredFields);
        if (!checkFields.isValid) {
            return addNewNotifcation(`Please fill in all required fields: ${checkFields.missingFields.map(item => (item.name)).join(', ')}`, 'warning');
        } else if (!instructionForm.instruction || instructionForm.instruction.trim() === '') {
            return addNewNotifcation('Please fill in all required fields: instruction', 'warning');
        }

        // check is model is deprecated or not
        const modelProvider = modelProviders.find(model => model.model_id === instructionObj.modelId);
        if (!modelProvider) {
            return addNewNotifcation(`Model "${instructionObj.modelId}" is deprecated. Please select another model.`, 'warning');
        }

        setBtnLoading(true);

        try {

            let res;
            if (copy) {
                res = await instructionService.createModel(instructionObj);
                addNewNotifcation('Instruction created successfully', 'success');
            } else {
                res = await instructionService.updateModel(instructionObj);
                addNewNotifcation('Instruction updated successfully', 'success');
            }
            onSuccess(res.data);

            setOpenModal(false);
        } catch (error) {

            if (error.response?.status === 409) {
                addNewNotifcation(`Instruction with ${instructionForm.unique_name} already exists`, 'danger');
            } else {
                addNewNotifcation('Something went wrong. Please try again', 'danger');
            }
        } finally {
            setBtnLoading(false);
        }
    };

    return (
        <Dialog open={openModal} onOpenChange={setOpenModal}>
            <DialogContent className="max-w-[850px] w-full">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-semibold">Manage Instruction</DialogTitle>
                    <DialogDescription className="text-md text-muted-foreground">Manage instruction data.</DialogDescription>
                </DialogHeader>

                <InstructionForm
                    handleReloadModels={handleReloadModels}
                    modelProviders={modelProviders}
                    form={instructionForm}
                    setForm={setInstructionForm}
                    update={!copy} />

                <DialogFooter className="flex justify-end space-x-5 mt-6">
                    <Button variant="outline" onClick={() => setOpenModal(false)} className="px-5 py-3 size-md border-solid shadow-none text-accent-foreground">Cancel</Button>
                    <Button
                        disabled={btnLoading || labHelper.validateUniqueName(instructionForm.unique_name).isValid === false}
                        onClick={handleUpdateInstruction}
                        className="px-5 py-3 text-md">
                        {btnLoading ? copy ? 'Saving instruction...' : 'Updating instruction...' : copy ? 'Save instruction' : 'Update instruction'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default UpdateInstructionDialog;